<template>
  <div class="apps-page">
    <div class="card card-custom gutter-b">
      <div class="card-header border-0 py-5">
        <div class="card-title align-items-start flex-column">
          <span class="card-label font-weight-bolder text-dark">Apps</span>
          <span class="text-muted mt-2 font-weight-bold font-size-sm">
            Adicione módulos e extensões para personalizar o Sistema Nuts
            conforme a necessidade da sua empresa.
          </span>
        </div>
      </div>

      <div class="card-body pt-0">
        <b-alert
          v-if="showCheckoutReturnMessage"
          show
          :variant="checkoutReturnVariant"
          class="mb-5"
        >
          {{ checkoutReturnMessage }}
        </b-alert>

        <div class="apps-toolbar">
          <div class="filter-tabs">
            <button
              v-for="filter in availableFilters"
              :key="filter.value"
              type="button"
              class="btn btn-sm"
              :class="
                selectedFilter === filter.value
                  ? 'btn-primary'
                  : 'btn-light-primary'
              "
              @click="selectedFilter = filter.value"
            >
              {{ filter.label }}
            </button>
          </div>

          <div class="search-wrapper">
            <MagnifyIcon :size="19" class="search-icon" />
            <input
              v-model.trim="search"
              type="text"
              class="form-control"
              placeholder="Buscar app"
            />
          </div>
        </div>

        <div v-if="isLoading" class="apps-loading">
          <b-spinner variant="primary" label="Carregando..." />
        </div>

        <div v-else-if="filteredApps.length === 0" class="apps-empty">
          <AppsIcon :size="36" />
          <strong>Nenhum app encontrado</strong>
          <span>Ajuste os filtros ou a busca para visualizar outros apps.</span>
        </div>

        <div v-else class="apps-grid">
          <div
            v-for="app in filteredApps"
            :key="appKey(app)"
            class="app-card"
            :class="appCardClass(app)"
          >
            <div class="app-card-header">
              <div class="app-card-icon-type">
                <div class="app-icon" :class="appTypeClass(app)">
                  <PuzzleIcon v-if="normalizedType(app) === 'plugin'" :size="24" />
                  <AppsIcon v-else :size="24" />
                </div>

                <div class="app-card-meta">
                  <span class="app-type">{{ appTypeLabel(app) }}</span>
                </div>
              </div>

              <span
                class="status-badge"
                :class="[
                  statusClass(app.status),
                  { 'badge-coming-soon': isComingSoonStatus(app.status) },
                ]"
                :title="statusTooltip(app.status)"
              >
                {{ statusLabel(app.status) }}
              </span>
            </div>

            <h3 class="app-card-title">{{ app.name }}</h3>

            <p class="app-description app-card-description">{{ appDescription(app) }}</p>

            <div
              v-if="normalizedRecommendedFor(app).length"
              class="app-recommended"
            >
              <strong>Recomendado para:</strong>
              <span>{{ normalizedRecommendedFor(app).join(" • ") }}</span>
            </div>

            <div v-if="normalizedTags(app).length" class="app-tags">
              <span
                v-for="tag in normalizedTags(app)"
                :key="`${appKey(app)}-${tag}`"
                class="app-tag"
              >
                {{ tag }}
              </span>
            </div>

            <div class="benefits">
              <strong>Benefícios:</strong>
              <ul>
                <li
                  v-for="benefit in normalizedBenefits(app)"
                  :key="benefit"
                >
                  {{ benefit }}
                </li>
              </ul>
            </div>

            <div class="app-card-footer">
              <div class="app-price" :class="{ 'app-price-per-quantity': isPerQuantity(app) }">
                <span>{{ monthlyPriceLabel(app) }}</span>
                <small v-if="isPerQuantity(app)">
                  /mês por {{ unitPriceLabel(app) }}
                </small>
                <small v-else>/mês</small>
              </div>

              <div v-if="showQuantitySelector(app)" class="quantity-section">
                <strong>{{ quantitySelectorLabel(app) }}:</strong>

                <div class="quantity-controls">
                  <button
                    type="button"
                    class="btn btn-light-primary btn-sm"
                    :disabled="selectedQuantity(app) <= minQuantity(app)"
                    @click="decreaseQuantity(app)"
                  >
                    -
                  </button>

                  <span class="quantity-value">{{ selectedQuantity(app) }}</span>

                  <button
                    type="button"
                    class="btn btn-light-primary btn-sm"
                    :disabled="selectedQuantity(app) >= maxQuantity(app)"
                    @click="increaseQuantity(app)"
                  >
                    +
                  </button>
                </div>

                <span class="quantity-total">
                  {{ quantityTotalLabel(app) }}:
                  <strong>{{ totalMonthlyPriceLabel(app) }}/mês</strong>
                </span>
              </div>

              <div v-if="showInstalledUsage(app)" class="installed-usage">
                <strong>
                  {{ installedQuantity(app) }}
                  {{ quantityLabelPlural(app, installedQuantity(app)) }} contratados
                </strong>
                <span>
                  {{ usedQuantity(app) }}
                  {{ quantityLabelPlural(app, usedQuantity(app)) }} em uso
                </span>
                <span>
                  {{ availableInstalledQuantity(app) }} disponível
                  {{ availableInstalledQuantity(app) === 1 ? '' : 's' }}
                </span>
              </div>

              <button
                v-if="showActionButton(app)"
                type="button"
                class="btn font-weight-bold"
                :class="actionButtonClass(app)"
                :disabled="
                  isActionDisabled(app) || loadingCheckoutCode === appCode(app)
                "
                @click="handleAction(app)"
              >
                <b-spinner
                  v-if="loadingCheckoutCode === appCode(app)"
                  small
                  class="mr-2"
                />
                <component
                  v-else
                  :is="actionIcon(app)"
                  :size="18"
                  class="mr-2"
                />
                {{ actionLabel(app) }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      id="contract-app-modal"
      ref="contractAppModal"
      centered
      hide-footer
      :title="contractModalTitle()"
    >
      <p class="mb-3" v-if="selectedApp">
        {{ contractModalText() }}
      </p>
      <div
        v-if="selectedApp && selectedCheckoutMode === 'add_quantity'"
        class="checkout-summary mb-4"
      >
        <span>Valor adicional:</span>
        <strong>
          {{ selectedAppQuantity }} x {{ monthlyPriceLabel(selectedApp) }} =
          {{ contractModalTotalLabel() }}/mês
        </strong>
      </div>
      <p class="text-muted mb-0">
        Você será redirecionado para o checkout seguro do Asaas. Após a
        confirmação do pagamento,
        {{ selectedCheckoutMode === 'add_quantity'
          ? 'a quantidade contratada será atualizada automaticamente.'
          : 'o app será liberado automaticamente para sua empresa.' }}
      </p>

      <div class="d-flex justify-content-end mt-7">
        <button
          type="button"
          class="btn btn-light-primary font-weight-bold mr-2"
          @click="hideContractModal"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isConfirmingCheckout"
          @click="confirmCheckout"
        >
          <b-spinner v-if="isConfirmingCheckout" small class="mr-2" />
          <CreditCardIcon v-else :size="18" class="mr-2" />
          Ir para pagamento
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import AppsIcon from "vue-material-design-icons/Apps.vue";
import CheckCircleIcon from "vue-material-design-icons/CheckCircleOutline.vue";
import ClockIcon from "vue-material-design-icons/ClockOutline.vue";
import CreditCardIcon from "vue-material-design-icons/CreditCardOutline.vue";
import MagnifyIcon from "vue-material-design-icons/Magnify.vue";
import OpenInNewIcon from "vue-material-design-icons/OpenInNew.vue";
import PuzzleIcon from "vue-material-design-icons/PuzzleOutline.vue";
import { mapGetters } from "vuex";

const STATUS = {
  INSTALLED: "installed",
  AVAILABLE: "available",
  PENDING_PAYMENT: "pending_payment",
  UNAVAILABLE: "unavailable",
  COMING_SOON: "coming_soon",
};
const USERS_EXTRA_CODE = "PLG_USUARIOS_EXTRA";
const MARKETPLACE_MODULE_SEGMENTS = {
  MOD_CRM: "CRM",
  MOD_FOOD: "Food",
  MOD_SERVICOS: "Serviços",
  MOD_TRANSPORTE: "Transporte",
};
const RECOMMENDED_FILTER_PRIORITY = [
  "crm",
  "food",
  "servicos",
  "transporte",
];

export default {
  name: "appsIndex",
  components: {
    AppsIcon,
    CheckCircleIcon,
    ClockIcon,
    CreditCardIcon,
    MagnifyIcon,
    OpenInNewIcon,
    PuzzleIcon,
  },
  data() {
    return {
      apps: [],
      isLoading: false,
      isConfirmingCheckout: false,
      loadingCheckoutCode: null,
      showCheckoutReturnMessage: false,
      checkoutReturnMessage: "",
      checkoutReturnVariant: "info",
      search: "",
      selectedFilter: "all",
      selectedApp: null,
      selectedAppQuantity: 1,
      selectedCheckoutMode: "contract",
      appQuantities: {},
    };
  },
  computed: {
    ...mapGetters(["isClienteNutsDireto"]),
    availableFilters() {
      return [
        { label: "Todos", value: "all" },
        { label: "Módulos", value: "module" },
        { label: "Plugins", value: "plugin" },
        ...this.recommendedCategoryFilters,
        { label: "Instalados", value: STATUS.INSTALLED },
        { label: "Disponíveis", value: STATUS.AVAILABLE },
        { label: "Em breve", value: "coming_soon_group" },
      ];
    },
    recommendedCategoryFilters() {
      const labelsByKey = {};

      this.apps.forEach((app) => {
        this.normalizedRecommendedFor(app).forEach((category) => {
          const key = this.segmentKey(category);
          if (key && !labelsByKey[key]) {
            labelsByKey[key] = category;
          }
        });
      });

      const categories = Object.keys(labelsByKey).map((key) => ({
        label: labelsByKey[key],
        value: `segment:${key}`,
      }));

      return categories.sort((first, second) => {
        const firstKey = first.value.replace("segment:", "");
        const secondKey = second.value.replace("segment:", "");
        const firstPriority = RECOMMENDED_FILTER_PRIORITY.indexOf(firstKey);
        const secondPriority = RECOMMENDED_FILTER_PRIORITY.indexOf(secondKey);

        if (firstPriority !== -1 || secondPriority !== -1) {
          if (firstPriority === -1) return 1;
          if (secondPriority === -1) return -1;
          if (firstPriority !== secondPriority) return firstPriority - secondPriority;
        }

        return first.label.localeCompare(second.label, "pt-BR", {
          sensitivity: "base",
        });
      });
    },
    canAccessMarketplace() {
      return this.isClienteNutsDireto;
    },
    filteredApps() {
      const term = this.normalizeText(this.search);

      return this.apps.filter((app) => {
        const type = this.normalizedType(app);
        const status = app.status;
        const selectedSegment = this.selectedRecommendedSegment();
        const matchesFilter =
          this.selectedFilter === "all" ||
          this.selectedFilter === type ||
          this.selectedFilter === status ||
          (this.selectedFilter === "coming_soon_group" &&
            this.isComingSoonStatus(status)) ||
          (selectedSegment && this.matchesRecommendedSegment(app, selectedSegment));
        const matchesSearch = !term || this.searchableText(app).includes(term);

        return matchesFilter && matchesSearch;
      });
    },
  },
  created() {
    if (!this.ensureMarketplaceAccess()) return;

    this.handleCheckoutReturn();
    this.fetchApps();
  },
  watch: {
    "$route.query.checkout"() {
      if (!this.ensureMarketplaceAccess()) return;

      if (this.handleCheckoutReturn()) {
        this.fetchApps();
      }
    },
    availableFilters(filters) {
      if (filters.some((filter) => filter.value === this.selectedFilter)) return;
      this.selectedFilter = "all";
    },
  },
  methods: {
    ensureMarketplaceAccess() {
      if (this.canAccessMarketplace) return true;

      this.$toast.warning("Esta funcionalidade não está disponível para sua conta.");
      this.$router.replace({ name: "dashboard" });
      return false;
    },
    async fetchApps() {
      if (!this.canAccessMarketplace) return;

      this.isLoading = true;

      try {
        const { data } = await this.$http.get("/apps");
        const apps = this.normalizeAppsResponse(data);
        this.apps = apps;
        this.initializeAppQuantities(apps);
      } catch (error) {
        this.$toast.warning("Não foi possível carregar os apps.");
      } finally {
        this.isLoading = false;
      }
    },
    normalizeAppsResponse(data) {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.data)) return data.data;
      if (Array.isArray(data?.apps)) return data.apps;
      return [];
    },
    handleCheckoutReturn() {
      const checkoutStatus = this.$route?.query?.checkout;
      const messages = {
        success:
          "Pagamento recebido ou em processamento. Em instantes o app será liberado após confirmação do Asaas.",
        cancel: "Checkout cancelado. Nenhum app foi contratado.",
        expired: "O checkout expirou. Gere um novo pagamento para continuar.",
      };
      const variants = {
        success: "info",
        cancel: "warning",
        expired: "warning",
      };

      this.showCheckoutReturnMessage = !!messages[checkoutStatus];
      this.checkoutReturnMessage = messages[checkoutStatus] || "";
      this.checkoutReturnVariant = variants[checkoutStatus] || "info";

      return this.showCheckoutReturnMessage;
    },
    normalizeText(value) {
      return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },
    normalizeStringArray(value) {
      if (Array.isArray(value)) {
        return value.map((item) => String(item || "").trim()).filter(Boolean);
      }

      if (typeof value === "string") {
        return value
          .split(/\n|,|;|\|/)
          .map((item) => item.trim())
          .filter(Boolean);
      }

      return [];
    },
    appDescription(app) {
      return (
        app.short_description ||
        app.description ||
        app.descricao_curta ||
        app.descricao ||
        ""
      );
    },
    normalizedRecommendedFor(app) {
      return this.normalizeStringArray(app.recommended_for || app.recommendedFor);
    },
    normalizedTags(app) {
      return this.normalizeStringArray(app.tags);
    },
    segmentKey(value) {
      return this.normalizeText(value || "").replace(/\s+/g, "-");
    },
    selectedRecommendedSegment() {
      if (!this.selectedFilter.startsWith("segment:")) return "";
      return this.selectedFilter.replace("segment:", "");
    },
    moduleSegmentKeys(app) {
      if (this.normalizedType(app) !== "module") return [];

      const keys = new Set();
      const code = this.normalizedAppCode(app);
      const mappedSegment = MARKETPLACE_MODULE_SEGMENTS[code];

      if (mappedSegment) {
        keys.add(this.segmentKey(mappedSegment));
      }

      keys.add(this.segmentKey(app.name));

      return Array.from(keys).filter(Boolean);
    },
    matchesRecommendedSegment(app, segment) {
      const recommendedKeys = this.normalizedRecommendedFor(app).map((category) =>
        this.segmentKey(category)
      );

      if (recommendedKeys.includes(segment)) return true;

      return this.moduleSegmentKeys(app).includes(segment);
    },
    searchableText(app) {
      const benefits = this.normalizedBenefits(app).join(" ");
      const recommendedFor = this.normalizedRecommendedFor(app).join(" ");
      const tags = this.normalizedTags(app).join(" ");

      return this.normalizeText(
        [app.name, this.appDescription(app), benefits, recommendedFor, tags]
          .filter(Boolean)
          .join(" ")
      );
    },
    normalizedType(app) {
      const type = this.normalizeText(app.type || app.tipo);
      if (type === "plugin") return "plugin";
      return "module";
    },
    appTypeLabel(app) {
      return this.normalizedType(app) === "plugin" ? "Plugin" : "Módulo";
    },
    appTypeClass(app) {
      return this.normalizedType(app) === "plugin"
        ? "app-icon-plugin"
        : "app-icon-module";
    },
    appKey(app) {
      return this.appCode(app) || app.name;
    },
    appCode(app) {
      return app.code || app.codigo || app.id;
    },
    normalizedAppCode(app) {
      return String(this.appCode(app) || "").toUpperCase();
    },
    isUsersExtraApp(app) {
      return this.normalizedAppCode(app) === USERS_EXTRA_CODE;
    },
    billingMode(app) {
      return this.normalizeText(app.billing_mode || app.modo_cobranca) ===
        "per_quantity"
        ? "per_quantity"
        : "fixed";
    },
    isPerQuantity(app) {
      return this.billingMode(app) === "per_quantity";
    },
    parseInteger(value, fallback = 0) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.floor(parsed) : fallback;
    },
    minQuantity(app) {
      if (!this.isPerQuantity(app)) return 1;

      const min = this.parseInteger(app.min_quantity, 1);
      return min > 0 ? min : 1;
    },
    maxQuantity(app) {
      if (!this.isPerQuantity(app)) return 1;

      const min = this.minQuantity(app);
      const max = this.parseInteger(app.max_quantity, min);
      const normalizedMax = max >= min ? max : min;

      if (this.canAddQuantity(app)) {
        return Math.max(min, normalizedMax - this.installedQuantity(app));
      }

      return normalizedMax;
    },
    quantityLabel(app) {
      if (this.isUsersExtraApp(app)) {
        return "usuário extra";
      }

      const label = String(app.quantity_label || "item").trim();
      return label || "item";
    },
    unitPriceLabel(app) {
      if (this.isUsersExtraApp(app)) {
        return "usuário adicional";
      }

      return String(app.unit_price_label || this.quantityLabel(app)).trim();
    },
    quantityLabelPlural(app, quantity) {
      if (this.isUsersExtraApp(app)) {
        return quantity === 1 ? "usuário extra" : "usuários extras";
      }

      const label = this.quantityLabel(app);

      if (quantity === 1 || label.toLowerCase().endsWith("s")) {
        return label;
      }

      return `${label}s`;
    },
    addQuantityLabelPlural(app, quantity) {
      if (this.isUsersExtraApp(app)) {
        return quantity === 1 ? "usuário" : "usuários";
      }

      return this.quantityLabelPlural(app, quantity);
    },
    installedQuantity(app) {
      return Math.max(0, this.parseInteger(app.installed_quantity, 0));
    },
    usedQuantity(app) {
      return Math.max(0, this.parseInteger(app.used_quantity, 0));
    },
    availableInstalledQuantity(app) {
      return Math.max(0, this.installedQuantity(app) - this.usedQuantity(app));
    },
    defaultQuantity(app) {
      return this.isPerQuantity(app) ? this.minQuantity(app) : 1;
    },
    normalizeSelectedQuantity(app, quantity) {
      if (!this.isPerQuantity(app)) return 1;

      const numericQuantity = this.parseInteger(quantity, this.defaultQuantity(app));
      return Math.min(this.maxQuantity(app), Math.max(this.minQuantity(app), numericQuantity));
    },
    selectedQuantity(app) {
      const code = this.appCode(app);

      if (!code) return this.defaultQuantity(app);

      return this.normalizeSelectedQuantity(app, this.appQuantities[code]);
    },
    setSelectedQuantity(app, quantity) {
      const code = this.appCode(app);
      if (!code) return;

      this.$set(
        this.appQuantities,
        code,
        this.normalizeSelectedQuantity(app, quantity)
      );
    },
    initializeAppQuantities(apps) {
      const quantities = {};

      apps.forEach((app) => {
        const code = this.appCode(app);
        if (!code) return;

        quantities[code] = this.defaultQuantity(app);
      });

      this.appQuantities = quantities;
    },
    increaseQuantity(app) {
      this.setSelectedQuantity(app, this.selectedQuantity(app) + 1);
    },
    decreaseQuantity(app) {
      this.setSelectedQuantity(app, this.selectedQuantity(app) - 1);
    },
    showQuantitySelector(app) {
      return (
        this.canCheckoutApp(app) &&
        this.isPerQuantity(app) &&
        (app.status === STATUS.AVAILABLE || this.canAddQuantity(app))
      );
    },
    showInstalledUsage(app) {
      return app.status === STATUS.INSTALLED && this.installedQuantity(app) > 0;
    },
    normalizedBenefits(app) {
      const benefits = app.benefits || app.beneficios || app.benefits_main;

      if (Array.isArray(benefits)) {
        return benefits.filter(Boolean);
      }

      if (typeof benefits === "string") {
        return benefits
          .split(/\n|;/)
          .map((benefit) => benefit.trim())
          .filter(Boolean);
      }

      return [];
    },
    monthlyPrice(app) {
      return Number(
        app.monthly_price ||
          app.valor_mensal ||
          app.price_monthly ||
          app.price ||
          app.valor ||
          0
      );
    },
    totalMonthlyPrice(app) {
      return this.monthlyPrice(app) * this.selectedQuantity(app);
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(value) || 0);
    },
    monthlyPriceLabel(app) {
      return this.formatCurrency(this.monthlyPrice(app));
    },
    totalMonthlyPriceLabel(app) {
      return this.formatCurrency(this.totalMonthlyPrice(app));
    },
    quantitySelectorLabel(app) {
      if (this.canAddQuantity(app)) {
        return "Quantidade adicional";
      }

      return `Quantidade de ${this.quantityLabelPlural(app, 2)}`;
    },
    quantityTotalLabel(app) {
      if (this.canAddQuantity(app)) {
        return "Total adicional";
      }

      return "Total mensal";
    },
    statusLabel(status) {
      const labels = {
        [STATUS.INSTALLED]: "Instalado",
        [STATUS.AVAILABLE]: "Disponível",
        [STATUS.PENDING_PAYMENT]: "Pendente",
        [STATUS.UNAVAILABLE]: "Em breve",
        [STATUS.COMING_SOON]: "Em breve",
      };

      return labels[status] || "Disponível";
    },
    statusTooltip(status) {
      const labels = {
        [STATUS.PENDING_PAYMENT]: "Aguardando confirmação de pagamento.",
      };

      return labels[status] || this.statusLabel(status);
    },
    statusClass(status) {
      const classes = {
        [STATUS.INSTALLED]: "status-installed",
        [STATUS.AVAILABLE]: "status-available",
        [STATUS.PENDING_PAYMENT]: "status-pending",
        [STATUS.UNAVAILABLE]: "status-unavailable",
        [STATUS.COMING_SOON]: "status-coming-soon",
      };

      return classes[status] || classes[STATUS.AVAILABLE];
    },
    appCardClass(app) {
      const status = app?.status;

      if (status === STATUS.AVAILABLE) return "app-card-available";
      if (status === STATUS.INSTALLED) return "app-card-installed";
      if (status === STATUS.PENDING_PAYMENT) return "app-card-pending";
      return "app-card--coming-soon";
    },
    isComingSoonStatus(status) {
      return [STATUS.UNAVAILABLE, STATUS.COMING_SOON].includes(status);
    },
    actionLabel(app) {
      if (app.status === STATUS.INSTALLED) {
        if (this.canAddQuantity(app)) {
          return `Contratar mais ${this.addQuantityLabelPlural(app, 2)}`;
        }

        return "Instalado";
      }
      if (app.status === STATUS.PENDING_PAYMENT) {
        return app.checkout_url ? "Continuar pagamento" : "Gerar pagamento";
      }
      if (this.isComingSoonStatus(app.status)) return "Em breve";

      if (this.isPerQuantity(app)) {
        const quantity = this.selectedQuantity(app);
        const label = this.quantityLabelPlural(app, quantity);
        return `Contratar ${quantity} ${label} por ${this.totalMonthlyPriceLabel(
          app
        )}/mês`;
      }

      return `Contratar por ${this.monthlyPriceLabel(app)}/mês`;
    },
    actionIcon(app) {
      if (app.status === STATUS.INSTALLED) {
        return this.canAddQuantity(app) ? "CreditCardIcon" : "CheckCircleIcon";
      }
      if (app.status === STATUS.PENDING_PAYMENT) return "OpenInNewIcon";
      if (this.isComingSoonStatus(app.status)) return "ClockIcon";

      return "CreditCardIcon";
    },
    actionButtonClass(app) {
      if (app.status === STATUS.AVAILABLE) return "btn-success";
      if (app.status === STATUS.PENDING_PAYMENT) return "btn-warning";
      if (app.status === STATUS.INSTALLED) {
        return this.canAddQuantity(app) ? "btn-success" : "btn-light-success";
      }

      return "btn-light-secondary btn-coming-soon";
    },
    isActionDisabled(app) {
      if (app.status === STATUS.INSTALLED) {
        return !this.canAddQuantity(app);
      }

      return [STATUS.UNAVAILABLE, STATUS.COMING_SOON].includes(app.status);
    },
    canAddQuantity(app) {
      return (
        !!app &&
        this.canAccessMarketplace &&
        app.status === STATUS.INSTALLED &&
        this.isPerQuantity(app) &&
        app.can_add_quantity !== false
      );
    },
    canCheckoutApp(app) {
      return (
        this.canAccessMarketplace &&
        ([STATUS.AVAILABLE, STATUS.PENDING_PAYMENT].includes(app.status) ||
          this.canAddQuantity(app))
      );
    },
    showActionButton(app) {
      if (
        [STATUS.INSTALLED, STATUS.UNAVAILABLE, STATUS.COMING_SOON].includes(
          app.status
        )
      ) {
        return true;
      }

      return this.canCheckoutApp(app);
    },
    handleAction(app) {
      if (!this.canCheckoutApp(app)) return;

      if (app.status === STATUS.PENDING_PAYMENT) {
        this.continuePayment(app);
        return;
      }

      if (app.status === STATUS.AVAILABLE) {
        this.selectedApp = app;
        this.selectedAppQuantity = this.selectedQuantity(app);
        this.selectedCheckoutMode = "contract";
        this.$refs.contractAppModal.show();
        return;
      }

      if (this.canAddQuantity(app)) {
        this.selectedApp = app;
        this.selectedAppQuantity = this.selectedQuantity(app);
        this.selectedCheckoutMode = "add_quantity";
        this.$refs.contractAppModal.show();
      }
    },
    async continuePayment(app) {
      if (!this.canCheckoutApp(app)) return;

      if (app.checkout_url) {
        window.location.href = app.checkout_url;
        return;
      }

      await this.startCheckout(app, this.selectedQuantity(app));
    },
    hideContractModal() {
      this.$refs.contractAppModal.hide();
      this.selectedApp = null;
      this.selectedAppQuantity = 1;
      this.selectedCheckoutMode = "contract";
    },
    contractModalTitle() {
      if (!this.selectedApp) return "Contratar app";

      if (this.selectedCheckoutMode === "add_quantity") {
        return "Contratar quantidade adicional";
      }

      return "Contratar app";
    },
    contractModalText() {
      if (!this.selectedApp) return "";

      if (this.selectedCheckoutMode === "add_quantity") {
        const quantity = this.selectedAppQuantity;
        const label = this.addQuantityLabelPlural(this.selectedApp, quantity);

        return `Você deseja adicionar ${quantity} ${label} ao app ${
          this.selectedApp.name
        }?`;
      }

      if (!this.isPerQuantity(this.selectedApp)) {
        return `Você deseja contratar o app ${this.selectedApp.name} por ${this.monthlyPriceLabel(this.selectedApp)}/mês?`;
      }

      const quantity = this.selectedAppQuantity;
      const label = this.quantityLabelPlural(this.selectedApp, quantity);
      const total = this.totalMonthlyPriceLabel(this.selectedApp);

      return `Você deseja contratar o app ${this.selectedApp.name} com ${quantity} ${label} por ${total}/mês?`;
    },
    contractModalTotalLabel() {
      if (!this.selectedApp) return this.formatCurrency(0);
      return this.formatCurrency(
        this.monthlyPrice(this.selectedApp) * this.selectedAppQuantity
      );
    },
    async confirmCheckout() {
      if (!this.selectedApp) return;
      if (!this.canCheckoutApp(this.selectedApp)) return;

      this.isConfirmingCheckout = true;
      await this.startCheckout(
        this.selectedApp,
        this.selectedAppQuantity,
        this.selectedCheckoutMode
      );
      this.isConfirmingCheckout = false;
    },
    checkoutQuantity(app, quantity) {
      if (!this.isPerQuantity(app)) return 1;
      return this.normalizeSelectedQuantity(app, quantity);
    },
    async startCheckout(app, quantity = 1, checkoutMode = "contract") {
      if (!this.canCheckoutApp(app)) {
        this.$toast.warning(
          "Esta funcionalidade não está disponível para sua conta."
        );
        return;
      }

      const code = this.appCode(app);

      if (!code) {
        this.$toast.warning("App sem código para contratação.");
        return;
      }

      this.loadingCheckoutCode = code;

      try {
        const payload = {
          quantity: this.checkoutQuantity(app, quantity),
        };

        if (checkoutMode === "add_quantity") {
          payload.checkout_mode = "add_quantity";
        }

        const { data } = await this.$http.post(
          `/apps/${code}/checkout`,
          payload
        );

        if (data?.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }

        this.$toast.warning("Checkout indisponível para este app.");
      } catch (error) {
        this.$toast.warning("Não foi possível iniciar o pagamento.");
      } finally {
        this.loadingCheckoutCode = null;
      }
    },
  },
};
</script>

<style scoped>
.apps-page {
  --apps-border: #e4e6ef;
  --apps-muted: #7e8299;
}

.apps-toolbar {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 24px;
}

.filter-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.search-wrapper {
  max-width: 320px;
  min-width: 240px;
  position: relative;
  width: 100%;
}

.search-icon {
  color: var(--apps-muted);
  left: 12px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.search-wrapper .form-control {
  padding-left: 40px;
}

.apps-loading,
.apps-empty {
  align-items: center;
  color: var(--apps-muted);
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  min-height: 240px;
  text-align: center;
}

.apps-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
}

.app-card {
  border: 1px solid var(--apps-border);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  min-height: 360px;
  padding: 22px;
}

.app-card-available {
  background: linear-gradient(180deg, #ffffff 0%, #f5fbf7 100%);
  border-color: #9fddba;
  box-shadow: 0 14px 30px rgba(28, 124, 72, 0.14);
  transform: translateY(-1px);
}

.app-card-available .app-icon-module,
.app-card-available .app-icon-plugin {
  background: #e9f7ef;
  color: #1c7c48;
}

.app-card-installed {
  border-color: #dfe9f3;
}

.app-card-pending {
  border-color: #ffe3af;
}

.app-card--coming-soon {
  background: #fffaf3;
  border: 1px dashed #f0b45b;
  opacity: 1;
}

.app-card-available .status-badge {
  background: #e4f6ea;
  color: #1c7c48;
}

.app-card-header {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 10px;
}

.app-card-icon-type {
  align-items: center;
  display: flex;
  gap: 10px;
  min-width: 0;
}

.app-card-meta {
  min-width: 0;
}

.app-card-title {
  color: #181c32;
  font-size: 1.18rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
  overflow: hidden;
  overflow-wrap: normal;
  white-space: normal;
  word-break: normal;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.app-icon {
  align-items: center;
  border-radius: 8px;
  display: flex;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.app-icon-module {
  background: #e9f7ef;
  color: #1c7c48;
}

.app-icon-plugin {
  background: #e8f4ff;
  color: #1f6fb2;
}

.app-type {
  color: var(--apps-muted);
  display: block;
  font-size: 0.77rem;
  font-weight: 700;
  text-transform: uppercase;
}

.status-badge {
  border-radius: 6px;
  font-size: 0.78rem;
  flex-shrink: 0;
  font-weight: 700;
  line-height: 1.2;
  max-width: 150px;
  padding: 5px 8px;
  text-align: center;
  white-space: normal;
}

.status-installed {
  background: #e9f7ef;
  color: #1c7c48;
}

.status-available {
  background: #e9f7ef;
  color: #1c7c48;
}

.status-pending {
  background: #fff4de;
  color: #b86a00;
}

.status-unavailable {
  background: #fff0d6;
  border: 1px solid #f0b45b;
  color: #9a5b00;
}

.status-coming-soon {
  background: #fff0d6;
  border: 1px solid #f0b45b;
  color: #9a5b00;
}

.app-card--coming-soon .app-card-title,
.app-card--coming-soon .app-card-description,
.app-card--coming-soon .benefits,
.app-card--coming-soon .app-price span,
.app-card--coming-soon .app-price small {
  color: #2f3742;
}

.app-card--coming-soon .app-type,
.app-card--coming-soon .app-tag,
.app-card--coming-soon .app-recommended {
  color: #5f6b7a;
}

.app-card--coming-soon .badge-coming-soon {
  background: #fff0d6;
  border: 1px solid #f0b45b;
  color: #9a5b00;
  font-weight: 700;
}

.app-card--coming-soon .app-card-footer {
  border-top-color: #f2d1a2;
}

.app-card--coming-soon .btn.btn-primary {
  background: #7e8299;
  border-color: #7e8299;
}

.btn-coming-soon,
.btn-coming-soon:disabled {
  background: #f4f6f8 !important;
  border-color: #d7dee8 !important;
  color: #5f6b7a !important;
  cursor: not-allowed;
}

.btn-light-success,
.btn-light-success:disabled {
  background: #e9f7ef !important;
  border-color: #c9ead5 !important;
  color: #1c7c48 !important;
}

.app-description {
  color: #3f4254;
  line-height: 1.55;
  margin: 20px 0 18px;
  min-height: 48px;
}

.app-recommended {
  color: #3f4254;
  display: flex;
  flex-direction: column;
  font-size: 0.92rem;
  gap: 2px;
  margin: -6px 0 14px;
}

.app-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: -2px 0 12px;
}

.app-tag {
  background: #f3f6f9;
  border-radius: 999px;
  color: #5e6278;
  font-size: 0.76rem;
  font-weight: 700;
  padding: 4px 10px;
}

.benefits {
  color: #3f4254;
  flex: 1;
}

.benefits ul {
  margin: 8px 0 0;
  padding-left: 18px;
}

.benefits li {
  margin-bottom: 5px;
}

.app-card-footer {
  border-top: 1px solid var(--apps-border);
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 20px;
  padding-top: 18px;
}

.app-price span {
  color: #181c32;
  font-size: 1.35rem;
  font-weight: 800;
}

.app-price small {
  color: var(--apps-muted);
  font-weight: 700;
  margin-left: 2px;
}

.app-price-per-quantity small {
  margin-left: 0;
}

.quantity-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.quantity-controls {
  align-items: center;
  display: inline-flex;
  gap: 10px;
}

.quantity-controls .btn {
  min-width: 36px;
  padding: 0.35rem 0.5rem;
}

.quantity-value {
  color: #181c32;
  font-size: 1rem;
  font-weight: 700;
  min-width: 24px;
  text-align: center;
}

.quantity-total {
  color: #3f4254;
  font-size: 0.92rem;
}

.installed-usage {
  color: #3f4254;
  display: flex;
  flex-direction: column;
  font-size: 0.92rem;
  gap: 3px;
}

.app-card-footer .btn {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  min-height: 42px;
  white-space: normal;
}

.checkout-summary {
  background: #f8fafc;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
}

.checkout-summary span {
  color: var(--apps-muted);
  font-weight: 700;
}

.checkout-summary strong {
  color: #181c32;
  font-size: 1rem;
}

@media (max-width: 767.98px) {
  .apps-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .search-wrapper {
    max-width: none;
    min-width: 0;
  }

  .apps-grid {
    grid-template-columns: 1fr;
  }

  .app-card-header {
    align-items: stretch;
    flex-direction: column;
  }

  .status-badge {
    max-width: none;
    width: fit-content;
  }
}
</style>
